<template>
    <v-container :class="[{ 'pa-0 ma-0': componentMode }]">

        <v-row v-if="loading">
            <v-col cols="12">
                <skeleton-loader type="chat-list" :count="componentMode ? 6 : 8" />
            </v-col>
        </v-row>
        <v-row v-else :dense="componentMode">
            <v-col cols="12" v-if="chats.length === 0" class="text-center mt-3">
                <small>{{ $t('common.no_chat_history') }}</small>
            </v-col>
            <v-col cols="6" v-if="!componentMode">
                <back-button />
            </v-col>
            <v-col cols="6" v-if="!componentMode">
                <kimsa-text-field :placeholder="$t('common.search')" :value="search" @change="search = $event"
                    icon="mdi-magnify" dense clearable no-elevation />
            </v-col>
            <v-col cols="12" :md="componentMode ? 12 : 6" :lg="componentMode ? 12 : 4" v-for="chat in chatsFiltered"
                :key="chat.id">
                <student-chat-card @click="onShowChat(chat)" :chat="chat" :withMenu="!componentMode"
                    @click:delete="showDeleteChat(chat)" is-company-chat />
            </v-col>
            <v-col cols="12" v-if="chatsFiltered.length === 0" class="text-center mt-3">
                <small>{{ $t('common.no_results') }}</small>
            </v-col>
        </v-row>

        <chat-dialog @hideChat="onHideChat" @deleted="loadChats" is-company-chat :clientId="clientId" ref="chat" />
        <kimsa-confirm ref="showDelete" :title="$t('common.delete_chat')" :content="$t('common.confirm_deleted_chat')"
            :persistent="false" :loading="loadingDelete" @confirm="deleteChat" @cancel="currentChat = null"
            :close-on-confirm="false" :alert-mode="true" />

    </v-container>
</template>

<script>

import StudentChatCard from '@/components/common/cards/StudentChatCard'
import ChatDialog from '@/components/student/ChatDialog'
import { db, getAllByIds } from '@/utils/Firebase'
import _map from 'lodash/map'
import _flatten from 'lodash/flatten'
import _uniq from 'lodash/uniq'
import BackButton from "@/components/common/BackButton";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaConfirm from '../../components/common/KimsaConfirm.vue'
import SkeletonLoader from '@/components/common/SkeletonLoader'

export default {
    components: {
        KimsaTextField,
        BackButton,
        StudentChatCard,
        ChatDialog,
        KimsaConfirm,
        SkeletonLoader,
    },
    props: {
        componentMode: {
            type: Boolean,
            default: false,
        },
        searchChat: {
            type: String,
            default: '',
        },
        chatType: {
            type: String,
            default: 'student'
        },
        groupFilter: {
            type: String,
            default: null,
        },
        coachIds: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            loading: false,
            chats: [],
            clientId: null,
            search: '',
            currentChat: null,
            loadingDelete: false,
        }
    },
    computed: {
        chatsByGroup(vm = this) {
            if (!vm.groupFilter) return vm.chats
            const coachIds = new Set(vm.coachIds)
            return vm.chats.filter(chat => {
                const other = (chat.participants || []).find(participant => participant.objectID !== vm.companyId)
                const isCoach = !!other && coachIds.has(other.objectID)
                return vm.groupFilter === 'coach' ? isCoach : !isCoach
            })
        },
        chatsFiltered(vm = this) {
            if (!vm.search || vm.search.length < 3) return vm.chatsByGroup
            return vm.chatsByGroup.filter(chat =>
                chat.participants.some(participant =>
                    participant.name?.toLowerCase().includes(vm.search.toLowerCase()) || participant.email?.toLowerCase().includes(vm.search.toLowerCase())
                )
            )
        },
        companyId(vm = this) {
            return vm.currentUser?.uid || null
        }
    },
    async mounted() {
        const vm = this
        vm.loadChats()
    },
    methods: {
        async loadChats(forceAll = false) {
            const vm = this
            vm.loading = true
            let allChats = []

            let query = db.collection('privateChats').where('participants', 'array-contains', vm.currentUser.uid)

            if (vm.componentMode && !forceAll) {
                query = query
                    .where('lastMessage.read', '==', false)
                    .where('lastMessage.from', '!=', vm.currentUser.uid)
                    .orderBy('lastMessage.from')
                    .orderBy('lastMessage.timestamp', 'desc')
            } else {
                query = query.orderBy('lastMessage.timestamp', 'desc')
            }

            if (vm.componentMode && !forceAll) query = query.limit(6)

            const chatRef = await query.get();
            chatRef.docs.forEach(chat => {
                let payload = {
                    ...chat.data(),
                    id: chat.id
                }
                allChats.push(payload)
            })

            if (vm.componentMode && allChats.length < 6) {
                let remainingChats = 6 - allChats.length;
                let readChatRef = await db.collection('privateChats')
                    .where('participants', 'array-contains', vm.currentUser.uid)
                    .orderBy('lastMessage.timestamp', 'desc')
                    .limit(remainingChats)
                    .get()
                readChatRef.docs.forEach(chat => {
                    allChats.push({
                        ...chat.data(),
                        id: chat.id
                    });
                });
            }

            allChats = allChats.filter(
                (chat, index, self) => self.findIndex(c => c.id === chat.id) === index
            );

            const allParticipantIDs = _uniq(_flatten(_map(allChats, 'participants'))).filter(id => !!id)
            let allUsers = []
            if (allParticipantIDs.length > 0 && !!allParticipantIDs?.[0]) {
                allUsers = await getAllByIds('users', allParticipantIDs)
            }

            for (let chat of allChats) {
                if (chat.lastMessage) {
                    chat.lastMessage.from = allUsers.find(user => user.objectID === chat.lastMessage?.from)
                    chat.participants = allUsers.filter(user => chat.participants.includes(user.objectID))
                }
            }

            vm.chats = allChats

            if (!vm.componentMode || forceAll) {
                let messagedNoRead = allChats.filter(chat => chat.lastMessage?.from?.objectID !== vm.currentUser.uid && !chat.lastMessage?.read).length
                let myMessagesNoRead = vm.account?.counter?.messages || 0
                if (messagedNoRead !== myMessagesNoRead) await db.collection('users').doc(vm.currentUser.uid).update({ 'counter.messages': messagedNoRead })
            }

            vm.loading = false
        },
        onShowChat(chat) {
            const vm = this
            const notMe = chat.participants.find(usr => usr.objectID !== vm.currentUser.uid)
            vm.clientId = notMe?.objectID || null
            vm.$refs.chat.show(chat.id, notMe)
        },
        onHideChat(payload = {}) {
            const vm = this
            vm.clientId = null
            vm.$refs.chat.client = null

            const closedChatId = payload?.chatId
            if (!closedChatId) return

            const chat = vm.chats.find(item => item.id === closedChatId)
            if (!chat?.lastMessage) return

            const fromId = chat.lastMessage.from?.objectID || chat.lastMessage.from
            const wasUnreadFromOther = fromId !== vm.currentUser.uid && !chat.lastMessage.read
            if (!wasUnreadFromOther) return

            // El contador lo recalcula onChatWrite al marcar lastMessage.read;
            // aca solo se refresca el estado local de la tarjeta.
            vm.$set(chat.lastMessage, 'read', true)
        },
        openChat({ chatId, client }) {
            const vm = this;
            vm.$refs.chat.show(chatId, client)
        },
        showDeleteChat(chat) {
            const vm = this;
            vm.currentChat = chat
            vm.$refs.showDelete.show()
        },
        async deleteChat() {
            const vm = this;
            vm.loadingDelete = true
            await db.collection('privateChats').doc(vm.currentChat.id).delete()
            vm.currentChat = null
            vm.toast(vm.$t('common.chat_deleted'))
            vm.$refs.showDelete?.hide()
            vm.loadingDelete = false
            this.loadChats()
        },
    },
    watch: {
        searchChat(val) {
            this.search = val
        },
    }
}
</script>
